import React, { useState } from 'react'
import {
  ClipboardCheck, Search, Plus, Edit, Trash2, Eye, CheckCircle, XCircle,
  Clock, AlertTriangle, Ban, BarChart3, Users, HelpCircle, X
} from 'lucide-react'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Input, Badge, Pagination,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui'
import Select, { SelectOption } from '../components/ui/select'
import { Switch } from '../components/ui/switch'
import { SearchableSelect } from '../components/ui/searchable-select'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useTests, useTest, useCreateTest, useUpdateTest, useDeleteTest,
  useTestQuestions, useCreateQuestion, useUpdateQuestion, useDeleteQuestion,
  useTestAssignments, useCreateAssignment, useDeleteAssignment,
  useTestResults, useTestResultsSummary,
  type Test, type TestFilters, type TestQuestion, type QuestionOption,
  type TestAssignment, type AssignmentFilters, type ResultFilters,
} from '../hooks/testing/useTesting'
import { useEmployees } from '../hooks/useEmployees'
import { useDepartments } from '../hooks/useDepartments'
import { usePositions } from '../hooks/usePositions'
import { APP_TIME_ZONE } from '@/utils/dateFormat'

// ==================== Helpers ====================

const CATEGORY_LABELS: Record<string, string> = {
  mandatory: 'Обязательный',
  certification: 'Аттестация',
  training: 'Обучение',
}

const CATEGORY_COLORS: Record<string, string> = {
  mandatory: 'bg-red-100 text-red-800',
  certification: 'bg-blue-100 text-blue-800',
  training: 'bg-green-100 text-green-800',
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  passed: { label: 'Сдан', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed: { label: 'Не сдан', color: 'bg-red-100 text-red-800', icon: XCircle },
  in_progress: { label: 'В процессе', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  not_started: { label: 'Не начат', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
  blocked: { label: 'Заблокирован', color: 'bg-purple-100 text-purple-800', icon: Ban },
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

// ==================== Test Form ====================

interface TestFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  test?: Test | null
  mode: 'create' | 'edit'
}

function TestForm({ open, onOpenChange, test, mode }: TestFormProps) {
  const createTest = useCreateTest()
  const updateTest = useUpdateTest()

  const [form, setForm] = useState({
    title: test?.title || '',
    description: test?.description || '',
    category: test?.category || 'training',
    time_limit_minutes: test?.time_limit_minutes ?? 30,
    passing_score: test?.passing_score ?? 70,
    max_attempts: test?.max_attempts ?? 3,
    cooldown_hours: test?.cooldown_hours ?? 24,
    questions_per_attempt: test?.questions_per_attempt ?? null as number | null,
    shuffle_questions: test?.shuffle_questions ?? true,
    shuffle_options: test?.shuffle_options ?? true,
    is_active: test?.is_active ?? true,
  })

  // Reset form when test changes
  React.useEffect(() => {
    if (open) {
      setForm({
        title: test?.title || '',
        description: test?.description || '',
        category: test?.category || 'training',
        time_limit_minutes: test?.time_limit_minutes ?? 30,
        passing_score: test?.passing_score ?? 70,
        max_attempts: test?.max_attempts ?? 3,
        cooldown_hours: test?.cooldown_hours ?? 24,
        questions_per_attempt: test?.questions_per_attempt ?? null,
        shuffle_questions: test?.shuffle_questions ?? true,
        shuffle_options: test?.shuffle_options ?? true,
        is_active: test?.is_active ?? true,
      })
    }
  }, [open, test])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) return

    try {
      // Backend treats null as "all questions"; omit the field for cleaner payload
      const payload: any = { ...form }
      if (payload.questions_per_attempt == null || payload.questions_per_attempt === 0) {
        payload.questions_per_attempt = null
      }
      if (mode === 'create') {
        const { is_active, ...createData } = payload
        await createTest.mutateAsync(createData)
      } else if (test) {
        await updateTest.mutateAsync({ id: test.id, data: payload })
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving test:', error)
    }
  }

  const isLoading = createTest.isPending || updateTest.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Создать тест' : 'Редактировать тест'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Заполните параметры нового теста' : 'Измените параметры теста'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Название *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Название теста"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Описание</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Описание теста..."
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Категория</label>
              <Select
                value={form.category}
                onChange={(e) => setForm(p => ({ ...p, category: e.target.value as 'mandatory' | 'certification' | 'training' }))}
              >
                <SelectOption value="training">Обучение</SelectOption>
                <SelectOption value="mandatory">Обязательный</SelectOption>
                <SelectOption value="certification">Аттестация</SelectOption>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Время (мин)</label>
              <Input
                type="number"
                min={1}
                value={form.time_limit_minutes}
                onChange={(e) => setForm(p => ({ ...p, time_limit_minutes: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Проходной балл (%)</label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.passing_score}
                onChange={(e) => setForm(p => ({ ...p, passing_score: Number(e.target.value) }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Макс. попыток</label>
              <Input
                type="number"
                min={1}
                value={form.max_attempts}
                onChange={(e) => setForm(p => ({ ...p, max_attempts: Number(e.target.value) }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Кулдаун (ч)</label>
              <Input
                type="number"
                min={0}
                value={form.cooldown_hours}
                onChange={(e) => setForm(p => ({ ...p, cooldown_hours: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Вопросов за попытку</label>
            <Input
              type="number"
              min={0}
              placeholder="Все вопросы теста"
              value={form.questions_per_attempt ?? ''}
              onChange={(e) => {
                const v = e.target.value
                setForm(p => ({ ...p, questions_per_attempt: v === '' ? null : Number(v) }))
              }}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Если задано — из всех вопросов случайно выбирается указанное количество. Оставьте пустым, чтобы показывать все.
            </p>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.shuffle_questions} onCheckedChange={(v) => setForm(p => ({ ...p, shuffle_questions: v }))} />
              <label className="text-sm">Перемешивать вопросы</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.shuffle_options} onCheckedChange={(v) => setForm(p => ({ ...p, shuffle_options: v }))} />
              <label className="text-sm">Перемешивать варианты</label>
            </div>
          </div>

          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
              <label className="text-sm">Тест активен</label>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading || !form.title.trim()}>
              {isLoading ? 'Сохранение...' : mode === 'create' ? 'Создать' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Question Form ====================

interface QuestionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  testId: string
  question?: TestQuestion | null
  mode: 'create' | 'edit'
}

function QuestionForm({ open, onOpenChange, testId, question, mode }: QuestionFormProps) {
  const createQuestion = useCreateQuestion()
  const updateQuestion = useUpdateQuestion()

  const [form, setForm] = useState({
    text: '',
    question_type: 'single_choice' as 'single_choice' | 'multiple_choice',
    points: 1,
    options: [
      { text: '', is_correct: true },
      { text: '', is_correct: false },
    ] as { text: string; is_correct: boolean }[],
  })

  React.useEffect(() => {
    if (open) {
      if (question && mode === 'edit') {
        setForm({
          text: question.text,
          question_type: question.question_type,
          points: question.points,
          options: question.question_options?.map(o => ({
            text: o.text,
            is_correct: o.is_correct,
          })) || [
            { text: '', is_correct: true },
            { text: '', is_correct: false },
          ],
        })
      } else {
        setForm({
          text: '',
          question_type: 'single_choice',
          points: 1,
          options: [
            { text: '', is_correct: true },
            { text: '', is_correct: false },
          ],
        })
      }
    }
  }, [open, question, mode])

  const addOption = () => {
    setForm(p => ({
      ...p,
      options: [...p.options, { text: '', is_correct: false }],
    }))
  }

  const removeOption = (index: number) => {
    if (form.options.length <= 2) return
    setForm(p => ({
      ...p,
      options: p.options.filter((_, i) => i !== index),
    }))
  }

  const updateOption = (index: number, field: 'text' | 'is_correct', value: any) => {
    setForm(p => {
      const newOptions = [...p.options]
      if (field === 'is_correct' && p.question_type === 'single_choice') {
        // For single choice, only one can be correct
        newOptions.forEach((o, i) => {
          o.is_correct = i === index
        })
      } else {
        newOptions[index] = { ...newOptions[index], [field]: value }
      }
      return { ...p, options: newOptions }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.text.trim()) return
    if (form.options.some(o => !o.text.trim())) return
    if (!form.options.some(o => o.is_correct)) return

    try {
      if (mode === 'create') {
        await createQuestion.mutateAsync({ testId, data: form })
      } else if (question) {
        await updateQuestion.mutateAsync({ testId, questionId: question.id, data: form })
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving question:', error)
    }
  }

  const isLoading = createQuestion.isPending || updateQuestion.isPending
  const hasCorrectAnswer = form.options.some(o => o.is_correct)
  const allOptionsFilled = form.options.every(o => o.text.trim())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Добавить вопрос' : 'Редактировать вопрос'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Текст вопроса *</label>
            <textarea
              value={form.text}
              onChange={(e) => setForm(p => ({ ...p, text: e.target.value }))}
              placeholder="Введите текст вопроса..."
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Тип вопроса</label>
              <Select
                value={form.question_type}
                onChange={(e) => {
                  const newType = e.target.value as 'single_choice' | 'multiple_choice'
                  setForm(p => {
                    // Reset correct answers when switching type
                    if (newType === 'single_choice') {
                      const newOptions = p.options.map((o, i) => ({ ...o, is_correct: i === 0 }))
                      return { ...p, question_type: newType, options: newOptions }
                    }
                    return { ...p, question_type: newType }
                  })
                }}
              >
                <SelectOption value="single_choice">Один ответ</SelectOption>
                <SelectOption value="multiple_choice">Несколько ответов</SelectOption>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Баллы</label>
              <Input
                type="number"
                min={1}
                value={form.points}
                onChange={(e) => setForm(p => ({ ...p, points: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Варианты ответов *</label>
              <Button type="button" variant="outline" size="sm" onClick={addOption}>
                <Plus className="h-3 w-3 mr-1" /> Вариант
              </Button>
            </div>
            <div className="space-y-2">
              {form.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type={form.question_type === 'single_choice' ? 'radio' : 'checkbox'}
                    name="correct_option"
                    checked={option.is_correct}
                    onChange={() => updateOption(index, 'is_correct', !option.is_correct)}
                    className="h-4 w-4"
                    title="Правильный ответ"
                  />
                  <Input
                    value={option.text}
                    onChange={(e) => updateOption(index, 'text', e.target.value)}
                    placeholder={`Вариант ${index + 1}`}
                    className="flex-1"
                  />
                  {form.options.length > 2 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeOption(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {!hasCorrectAnswer && (
              <p className="text-sm text-red-500 mt-1">Отметьте хотя бы один правильный ответ</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading || !form.text.trim() || !allOptionsFilled || !hasCorrectAnswer}>
              {isLoading ? 'Сохранение...' : mode === 'create' ? 'Добавить' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Questions Panel ====================

interface QuestionsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  test: Test
}

function QuestionsPanel({ open, onOpenChange, test }: QuestionsPanelProps) {
  const { data: questionsData, isLoading } = useTestQuestions(test.id)
  const deleteQuestion = useDeleteQuestion()

  const [isQuestionFormOpen, setIsQuestionFormOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<TestQuestion | null>(null)
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(null)

  const questions: TestQuestion[] = Array.isArray(questionsData) ? questionsData : questionsData?.data || []

  const handleDeleteQuestion = async () => {
    if (!deletingQuestionId) return
    try {
      await deleteQuestion.mutateAsync({ testId: test.id, questionId: deletingQuestionId })
      setDeletingQuestionId(null)
    } catch (error) {
      console.error('Error deleting question:', error)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Вопросы: {test.title}</DialogTitle>
            <DialogDescription>
              {questions.length} {questions.length === 1 ? 'вопрос' : 'вопросов'} | Проходной балл: {test.passing_score}%
            </DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <Button size="sm" onClick={() => { setEditingQuestion(null); setIsQuestionFormOpen(true) }}>
              <Plus className="h-4 w-4 mr-1" /> Добавить вопрос
            </Button>
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Загрузка вопросов...</div>
          ) : questions.length === 0 ? (
            <div className="py-8 text-center">
              <HelpCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">Нет вопросов. Добавьте первый вопрос.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {questions.map((q, index) => (
                <Card key={q.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                          <Badge variant="outline" className="text-xs">
                            {q.question_type === 'single_choice' ? 'Один ответ' : 'Несколько'}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">{q.points} б.</Badge>
                        </div>
                        <p className="text-sm font-medium">{q.text}</p>
                        {q.question_options && (
                          <div className="mt-2 space-y-1">
                            {q.question_options.map((opt, oi) => (
                              <div key={oi} className={`text-xs px-2 py-1 rounded ${opt.is_correct ? 'bg-green-50 text-green-700' : 'text-muted-foreground'}`}>
                                {opt.is_correct ? '✓' : '○'} {opt.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingQuestion(q); setIsQuestionFormOpen(true) }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeletingQuestionId(q.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Закрыть</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuestionForm
        open={isQuestionFormOpen}
        onOpenChange={setIsQuestionFormOpen}
        testId={test.id}
        question={editingQuestion}
        mode={editingQuestion ? 'edit' : 'create'}
      />

      {/* Delete question confirm */}
      <Dialog open={!!deletingQuestionId} onOpenChange={() => setDeletingQuestionId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить вопрос?</DialogTitle>
            <DialogDescription>Это действие нельзя отменить.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingQuestionId(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDeleteQuestion} disabled={deleteQuestion.isPending}>
              {deleteQuestion.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Assignment Form ====================

interface AssignmentFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function AssignmentForm({ open, onOpenChange }: AssignmentFormProps) {
  const createAssignment = useCreateAssignment()
  const { data: testsData } = useTests({ limit: 100, is_active: 'true' })
  const { data: employeesData } = useEmployees({ limit: 10000 })
  const { data: departmentsData } = useDepartments({ limit: 100 })
  const { data: positionsData } = usePositions({ limit: 100 })

  const [form, setForm] = useState({
    test_id: '',
    assign_type: 'employee' as 'employee' | 'department' | 'position',
    target_id: null as string | null,
    is_required: true,
    deadline: '',
  })

  React.useEffect(() => {
    if (open) {
      setForm({ test_id: '', assign_type: 'employee', target_id: null, is_required: true, deadline: '' })
    }
  }, [open])

  const targetOptions = React.useMemo(() => {
    if (form.assign_type === 'employee') {
      return (employeesData?.data || []).map((e: any) => ({
        value: e.id,
        label: `${e.last_name} ${e.first_name}`,
        searchText: `${e.last_name} ${e.first_name} ${e.employee_number}`,
      }))
    }
    if (form.assign_type === 'department') {
      return (departmentsData?.data || []).map((d: any) => ({
        value: d.id,
        label: d.name,
      }))
    }
    if (form.assign_type === 'position') {
      return (positionsData?.data || []).map((p: any) => ({
        value: p.id,
        label: p.name,
      }))
    }
    return []
  }, [form.assign_type, employeesData, departmentsData, positionsData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.test_id || !form.target_id) return

    const payload: any = {
      test_id: form.test_id,
      is_required: form.is_required,
    }
    if (form.deadline) payload.deadline = form.deadline
    if (form.assign_type === 'employee') payload.employee_id = form.target_id
    if (form.assign_type === 'department') payload.department_id = form.target_id
    if (form.assign_type === 'position') payload.position_id = form.target_id

    try {
      await createAssignment.mutateAsync(payload)
      onOpenChange(false)
    } catch (error) {
      console.error('Error creating assignment:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Назначить тест</DialogTitle>
          <DialogDescription>Выберите тест и кому его назначить</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Тест *</label>
            <Select
              value={form.test_id}
              onChange={(e) => setForm(p => ({ ...p, test_id: e.target.value }))}
            >
              <SelectOption value="">Выберите тест</SelectOption>
              {(testsData?.data || []).map((t: Test) => (
                <SelectOption key={t.id} value={t.id}>{t.title}</SelectOption>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Тип назначения</label>
            <Select
              value={form.assign_type}
              onChange={(e) => setForm(p => ({ ...p, assign_type: e.target.value as any, target_id: null }))}
            >
              <SelectOption value="employee">Сотруднику</SelectOption>
              <SelectOption value="department">Отделу</SelectOption>
              <SelectOption value="position">Должности</SelectOption>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">
              {form.assign_type === 'employee' ? 'Сотрудник' : form.assign_type === 'department' ? 'Отдел' : 'Должность'} *
            </label>
            <SearchableSelect
              value={form.target_id}
              onChange={(v) => setForm(p => ({ ...p, target_id: v }))}
              options={targetOptions}
              placeholder="Выберите..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Дедлайн</label>
              <Input
                type="date"
                value={form.deadline}
                onChange={(e) => setForm(p => ({ ...p, deadline: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.is_required} onCheckedChange={(v) => setForm(p => ({ ...p, is_required: v }))} />
              <label className="text-sm">Обязательный</label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
            <Button type="submit" disabled={createAssignment.isPending || !form.test_id || !form.target_id}>
              {createAssignment.isPending ? 'Назначение...' : 'Назначить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Tests Tab ====================

function TestsTab() {
  const [filters, setFilters] = useState<TestFilters>({ page: 1, limit: 20, sort: 'created_at:desc' })
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')

  const { data, isLoading, error, refetch } = useTests({
    ...filters,
    search: searchTerm || undefined,
    category: categoryFilter || undefined,
    is_active: activeFilter || undefined,
  })

  const deleteTest = useDeleteTest()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingTest, setEditingTest] = useState<Test | null>(null)
  const [questionsTest, setQuestionsTest] = useState<Test | null>(null)
  const [deletingTest, setDeletingTest] = useState<Test | null>(null)

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setFilters(p => ({ ...p, page: 1 }))
  }

  const handleDelete = async () => {
    if (!deletingTest) return
    try {
      await deleteTest.mutateAsync(deletingTest.id)
      setDeletingTest(null)
    } catch (error) {
      console.error('Error deleting test:', error)
    }
  }

  if (error) return <ErrorState title="Ошибка загрузки тестов" onAction={refetch} />

  return (
    <>
      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по названию..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все категории</SelectOption>
              <SelectOption value="mandatory">Обязательный</SelectOption>
              <SelectOption value="certification">Аттестация</SelectOption>
              <SelectOption value="training">Обучение</SelectOption>
            </Select>
            <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="true">Активные</SelectOption>
              <SelectOption value="false">Неактивные</SelectOption>
            </Select>
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Создать тест
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список тестов</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Категория</TableHead>
                    <TableHead>Вопросов</TableHead>
                    <TableHead>Проходной балл</TableHead>
                    <TableHead>Время</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((test: Test) => (
                    <TableRow key={test.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.title}</p>
                          {test.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[300px]">{test.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${CATEGORY_COLORS[test.category] || ''}`}>
                          {CATEGORY_LABELS[test.category] || test.category}
                        </span>
                      </TableCell>
                      <TableCell>{test._count?.test_questions || 0}</TableCell>
                      <TableCell>{test.passing_score}%</TableCell>
                      <TableCell>{test.time_limit_minutes} мин</TableCell>
                      <TableCell>
                        <Badge variant={test.is_active ? 'default' : 'secondary'}>
                          {test.is_active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(test.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" title="Вопросы" onClick={() => setQuestionsTest(test)}>
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Редактировать" onClick={() => setEditingTest(test)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Удалить" onClick={() => setDeletingTest(test)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardCheck className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Тесты не найдены</h3>
              <p className="mt-1 text-sm text-muted-foreground">Создайте первый тест для начала работы.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <TestForm open={isCreateOpen} onOpenChange={setIsCreateOpen} mode="create" />

      <TestForm
        open={!!editingTest}
        onOpenChange={(open) => { if (!open) setEditingTest(null) }}
        test={editingTest}
        mode="edit"
      />

      {questionsTest && (
        <QuestionsPanel
          open={!!questionsTest}
          onOpenChange={(open) => { if (!open) setQuestionsTest(null) }}
          test={questionsTest}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={!!deletingTest} onOpenChange={() => setDeletingTest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить тест?</DialogTitle>
            <DialogDescription>
              Тест «{deletingTest?.title}» будет удалён. Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTest(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteTest.isPending}>
              {deleteTest.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Assignments Tab ====================

function AssignmentsTab() {
  const [filters, setFilters] = useState<AssignmentFilters>({ page: 1, limit: 20, sort: 'created_at:desc' })
  const [testFilter, setTestFilter] = useState('')
  const [deptFilter, setDeptFilter] = useState('')

  const { data: testsData } = useTests({ limit: 100 })
  const { data: departmentsData } = useDepartments({ limit: 100 })

  const { data, isLoading, error, refetch } = useTestAssignments({
    ...filters,
    test_id: testFilter || undefined,
    department_id: deptFilter || undefined,
  })

  const deleteAssignment = useDeleteAssignment()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!deletingId) return
    try {
      await deleteAssignment.mutateAsync(deletingId)
      setDeletingId(null)
    } catch (error) {
      console.error('Error deleting assignment:', error)
    }
  }

  const getAssignTarget = (a: TestAssignment) => {
    if (a.employees) return `${a.employees.last_name} ${a.employees.first_name}`
    if (a.departments) return `Отдел: ${a.departments.name}`
    if (a.positions) return `Должность: ${a.positions.name}`
    return '—'
  }

  if (error) return <ErrorState title="Ошибка загрузки назначений" onAction={refetch} />

  return (
    <>
      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Select value={testFilter} onChange={(e) => { setTestFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все тесты</SelectOption>
              {(testsData?.data || []).map((t: Test) => (
                <SelectOption key={t.id} value={t.id}>{t.title}</SelectOption>
              ))}
            </Select>
            <Select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все отделы</SelectOption>
              {(departmentsData?.data || []).map((d: any) => (
                <SelectOption key={d.id} value={d.id}>{d.name}</SelectOption>
              ))}
            </Select>
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Назначить тест
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Назначения</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Тест</TableHead>
                    <TableHead>Кому назначен</TableHead>
                    <TableHead>Обязательный</TableHead>
                    <TableHead>Дедлайн</TableHead>
                    <TableHead>Дата назначения</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((a: TestAssignment) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.tests?.title || '—'}</TableCell>
                      <TableCell>{getAssignTarget(a)}</TableCell>
                      <TableCell>
                        <Badge variant={a.is_required ? 'default' : 'outline'}>
                          {a.is_required ? 'Да' : 'Нет'}
                        </Badge>
                      </TableCell>
                      <TableCell>{a.deadline ? formatDate(a.deadline) : '—'}</TableCell>
                      <TableCell className="text-sm">{formatDate(a.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" title="Удалить" onClick={() => setDeletingId(a.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Нет назначений</h3>
              <p className="mt-1 text-sm text-muted-foreground">Назначьте тест сотрудникам или отделам.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignmentForm open={isCreateOpen} onOpenChange={setIsCreateOpen} />

      {/* Delete confirm */}
      <Dialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить назначение?</DialogTitle>
            <DialogDescription>Назначение теста будет удалено.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteAssignment.isPending}>
              {deleteAssignment.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Results Tab ====================

function ResultsTab() {
  const [filters, setFilters] = useState<ResultFilters>({ page: 1, limit: 20, sort: 'last_attempt_at:desc' })
  const [searchTerm, setSearchTerm] = useState('')
  const [testFilter, setTestFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const { data: testsData } = useTests({ limit: 100 })
  const { data: summaryData } = useTestResultsSummary()

  const { data, isLoading, error, refetch } = useTestResults({
    ...filters,
    search: searchTerm || undefined,
    test_id: testFilter || undefined,
    status: statusFilter || undefined,
  })

  if (error) return <ErrorState title="Ошибка загрузки результатов" onAction={refetch} />

  return (
    <>
      {/* Summary cards */}
      {summaryData && summaryData.tests && summaryData.tests.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {summaryData.tests.map((s) => (
            <Card key={s.test_id}>
              <CardContent className="p-4">
                <h4 className="font-medium text-sm truncate mb-2">{s.title}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span>Назначено: {s.total_assigned}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>Сдали: {s.passed}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <XCircle className="h-3 w-3 text-red-500" />
                    <span>Не сдали: {s.failed}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BarChart3 className="h-3 w-3 text-blue-500" />
                    <span>Сдаваемость: {s.pass_rate}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по ФИО..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}
                className="pl-10"
              />
            </div>
            <Select value={testFilter} onChange={(e) => { setTestFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все тесты</SelectOption>
              {(testsData?.data || []).map((t: Test) => (
                <SelectOption key={t.id} value={t.id}>{t.title}</SelectOption>
              ))}
            </Select>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="passed">Сдан</SelectOption>
              <SelectOption value="failed">Не сдан</SelectOption>
              <SelectOption value="in_progress">В процессе</SelectOption>
              <SelectOption value="not_started">Не начат</SelectOption>
              <SelectOption value="blocked">Заблокирован</SelectOption>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Результаты тестирования</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Сотрудник</TableHead>
                    <TableHead>Тест</TableHead>
                    <TableHead>Попыток</TableHead>
                    <TableHead>Лучший балл</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Последняя попытка</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((r: any, idx: number) => {
                    const statusConf = STATUS_CONFIG[r.status] || STATUS_CONFIG.not_started
                    return (
                      <TableRow key={`${r.employee_id}-${r.test_id}-${idx}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{r.employee_name}</p>
                            {r.department_name && (
                              <p className="text-xs text-muted-foreground">{r.department_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{r.test_title}</TableCell>
                        <TableCell>{r.attempts_used}</TableCell>
                        <TableCell className="font-medium">{r.best_score}%</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${statusConf.color}`}>
                            <statusConf.icon className="h-3 w-3" />
                            {statusConf.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {r.last_attempt_at ? formatDate(r.last_attempt_at) : '—'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Нет результатов</h3>
              <p className="mt-1 text-sm text-muted-foreground">Результаты появятся после прохождения тестов.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}

// ==================== Main Page ====================

export function Testing() {
  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Тестирование</h1>
          <p className="text-muted-foreground">
            Управление тестами, назначениями и результатами
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tests">Тесты</TabsTrigger>
          <TabsTrigger value="assignments">Назначения</TabsTrigger>
          <TabsTrigger value="results">Результаты</TabsTrigger>
        </TabsList>

        <TabsContent value="tests">
          <TestsTab />
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentsTab />
        </TabsContent>

        <TabsContent value="results">
          <ResultsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
